if (!customElements.get('cart-checkout-consents')) {
  class CartCheckoutConsents extends HTMLElement {
    connectedCallback() {
      this.storageKey = this.dataset.storageKey || 'gotta-cart-consents';
      this.termsInput = this.querySelector('[data-consent="terms"]');
      this.commercialInput = this.querySelector('[data-consent="commercial"]');
      this.errorEl = this.querySelector('.cart-checkout-consents__error');
      this.seeMoreBtn = this.querySelector('[data-consent-toggle="commercial-more"]');

      this.restoreState();
      this.bindEvents();
      this.syncDynamicCheckout();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    bindEvents() {
      this.onChange = this.handleChange.bind(this);
      this.onSeeMore = this.handleSeeMore.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onDocumentSubmit = this.handleDocumentSubmit.bind(this);
      this.onPageShow = this.resetCheckoutButtons.bind(this);

      this.addEventListener('change', this.onChange);
      if (this.seeMoreBtn) this.seeMoreBtn.addEventListener('click', this.onSeeMore);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('submit', this.onDocumentSubmit, true);
      window.addEventListener('pageshow', this.onPageShow);
    }

    removeEventListeners() {
      this.removeEventListener('change', this.onChange);
      if (this.seeMoreBtn) this.seeMoreBtn.removeEventListener('click', this.onSeeMore);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    getFormId() {
      return this.termsInput?.getAttribute('form') || this.closest('form')?.id || '';
    }

    restoreState() {
      let saved = {};
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
      } catch (e) {
        saved = {};
      }

      if (this.termsInput && typeof saved.terms === 'boolean') {
        this.termsInput.checked = saved.terms;
      }
      if (this.commercialInput && typeof saved.commercial === 'boolean') {
        this.commercialInput.checked = saved.commercial;
      }

      this.clearError();
      this.syncDynamicCheckout();
    }

    persistState() {
      const state = {
        terms: Boolean(this.termsInput?.checked),
        commercial: Boolean(this.commercialInput?.checked),
      };
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(state));
      } catch (e) {
        // Ignore quota / private mode errors
      }
    }

    handleChange() {
      this.persistState();
      if (this.termsInput?.checked) this.clearError();
      this.syncDynamicCheckout();
    }

    handleSeeMore(event) {
      event.preventDefault();
      event.stopPropagation();
      const moreId = this.seeMoreBtn.getAttribute('aria-controls');
      const moreEl = moreId ? document.getElementById(moreId) : null;
      if (!moreEl) return;

      const expanded = this.seeMoreBtn.getAttribute('aria-expanded') === 'true';
      this.seeMoreBtn.setAttribute('aria-expanded', String(!expanded));
      moreEl.hidden = expanded;
    }

    isCheckoutTrigger(target) {
      if (!target) return false;
      const button = target.closest('button, input[type="submit"]');
      if (!button) return false;
      if (button.name === 'checkout') return true;
      if (button.id === 'checkout' || button.id === 'CartDrawer-Checkout') return true;
      return false;
    }

    belongsToThisInstance(trigger) {
      const formId = this.getFormId();
      if (!formId) return true;
      const triggerForm = trigger.getAttribute('form') || trigger.closest('form')?.id;
      return !triggerForm || triggerForm === formId;
    }

    handleDocumentClick(event) {
      if (!this.isCheckoutTrigger(event.target)) return;
      const button = event.target.closest('button, input[type="submit"]');
      if (!this.belongsToThisInstance(button)) return;

      if (button.getAttribute('aria-busy') === 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      if (!this.validate()) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const formId = this.getFormId();
      const form = formId ? document.getElementById(formId) : button.form;
      if (!(form instanceof HTMLFormElement) || typeof form.requestSubmit !== 'function') return;

      // The checkout button sits outside the cart form. Marking it busy during
      // this click (pointer-events: none) drops the submitter, so Shopify never
      // receives name=checkout and the shopper stays off the checkout page.
      event.preventDefault();
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }
      form.requestSubmit(button);
      this.setCheckoutBusy(button);
    }

    handleDocumentSubmit(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const formId = this.getFormId();
      if (formId && form.id !== formId) return;

      const submitter = event.submitter;
      const isCheckout =
        (submitter && (submitter.name === 'checkout' || this.isCheckoutTrigger(submitter))) ||
        Boolean(form.querySelector('[name="checkout"]'));

      if (!isCheckout) return;
      if (this.validate()) return;

      event.preventDefault();
      event.stopPropagation();
    }

    validate() {
      if (!this.termsInput || this.termsInput.checked) {
        this.clearError();
        return true;
      }

      this.showError();
      document.dispatchEvent(
        new CustomEvent('gotta:checkout-blocked', {
          detail: {
            checkout_path: this.getFormId() === 'CartDrawer-Form' ? 'cart_drawer' : 'cart_page',
            checkout_blocker: 'terms_not_accepted',
          },
        })
      );
      this.termsInput.focus({ preventScroll: false });
      return false;
    }

    showError() {
      this.classList.add('cart-checkout-consents--invalid');
      if (this.errorEl) this.errorEl.hidden = false;
      if (this.termsInput) this.termsInput.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      this.classList.remove('cart-checkout-consents--invalid');
      if (this.errorEl) this.errorEl.hidden = true;
      if (this.termsInput) this.termsInput.removeAttribute('aria-invalid');
    }

    setCheckoutBusy(button) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      window.setTimeout(() => this.resetCheckoutButton(button), 12000);
    }

    resetCheckoutButton(button) {
      if (!button?.isConnected) return;
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }

    resetCheckoutButtons() {
      const formId = this.getFormId();
      document.querySelectorAll('.cart__checkout-button').forEach((button) => {
        const buttonFormId = button.getAttribute('form') || button.closest('form')?.id || '';
        if (!formId || buttonFormId === formId) this.resetCheckoutButton(button);
      });
    }

    syncDynamicCheckout() {
      const accepted = Boolean(this.termsInput?.checked);
      document.querySelectorAll('.additional-checkout-buttons, .cart__dynamic-checkout-buttons').forEach((el) => {
        el.classList.toggle('cart-checkout-consents__dynamic--blocked', !accepted);
        el.setAttribute('aria-hidden', accepted ? 'false' : 'true');
        el.querySelectorAll('button, a, input').forEach((control) => {
          if ('disabled' in control) control.disabled = !accepted;
          if (!accepted) control.setAttribute('tabindex', '-1');
          else control.removeAttribute('tabindex');
        });
      });
    }
  }

  customElements.define('cart-checkout-consents', CartCheckoutConsents);
}
